/**
 * VariablePicker — Dropdown zur Auswahl von Mustache-Variablen.
 * @since v2.514.0 (#1173)
 *
 * Props:
 *   - scopes: VARIABLE_SCOPES_CATALOG vom Backend
 *   - priorSteps: Array vorheriger Steps (für steps.<id>.outputs.*)
 *   - onPick: Callback (insertText: string) — fügt `{{...}}` ein
 */
import { useState, useRef, useEffect } from 'react';

export default function VariablePicker({ scopes = [], priorSteps = [], onPick }) {
  const [open, setOpen] = useState(false);
  const [activeScope, setActiveScope] = useState('event');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function pick(path) {
    const tpl = `{{${path}}}`;
    onPick && onPick(tpl);
    setOpen(false);
  }

  const scopeMeta = scopes.find((s) => s.scope === activeScope);
  let suggestions = scopeMeta?.examples || [];
  if (activeScope === 'steps' && priorSteps.length) {
    suggestions = priorSteps.flatMap((s) => [
      `steps.${s.id}.outputs.result`,
      `steps.${s.id}.outputs.relevant`,
      `steps.${s.id}.outputs.value`,
    ]);
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="px-2 py-1 text-xs border border-[var(--border-subtle,#E5E7EB)] rounded hover:bg-gray-50 text-[var(--text-secondary)]"
      >
        {'{{ var }}'}
      </button>
      {open && (
        <div className="absolute z-30 right-0 mt-1 w-[420px] bg-white border border-[var(--border-subtle,#E5E7EB)] rounded-lg shadow-lg">
          <div className="flex border-b border-[var(--border-subtle,#E5E7EB)] bg-gray-50">
            {scopes.map((s) => (
              <button
                key={s.scope}
                type="button"
                onClick={() => setActiveScope(s.scope)}
                className={`flex-1 px-2 py-1.5 text-[11px] font-medium ${activeScope === s.scope ? 'bg-white text-[#338edc] border-b-2 border-[#338edc]' : 'text-[var(--text-tertiary)]'}`}
              >
                {s.scope}
              </button>
            ))}
          </div>
          <div className="p-2 max-h-[280px] overflow-y-auto">
            {scopeMeta?.description && (
              <div className="text-[10px] text-[var(--text-tertiary)] mb-2">{scopeMeta.description}</div>
            )}
            {suggestions.length === 0 ? (
              <div className="text-xs text-[var(--text-tertiary)] italic">Keine Vorschläge — manuell eingeben.</div>
            ) : (
              <div className="space-y-1">
                {suggestions.map((path) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => pick(path)}
                    className="block w-full text-left px-2 py-1 text-xs font-mono rounded hover:bg-[#338edc]/10 text-[var(--text-secondary)]"
                  >
                    {`{{${path}}}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
